import { HugeiconsIcon } from "@hugeicons/react";
import { CheckmarkCircle02Icon } from "@hugeicons/core-free-icons";
import {
    Box,
    Button,
    Collapse,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

interface StepItem {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    isCompleted: boolean;
    actionText: string;
    destination?: string;
    onAction?: () => void;
    allowIgnore: boolean;
}

interface Section {
    title: string;
    description: string;
    steps: StepItem[];
}

interface Props {
    section: Section;
    expandedStep: string | null;
    onStepClick: (id: string) => void;
}

interface CustomStepIconProps {
    active: boolean;
    completed: boolean;
    icon: React.ReactNode;
}

const CustomStepIcon = ({
    active,
    completed,
    icon,
}: CustomStepIconProps) => {
    if (completed) {
        return (
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "success.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    zIndex: 2,
                }}
            >
                <HugeiconsIcon
                    icon={CheckmarkCircle02Icon}
                    size={20}
                    color="white"
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: active ? "primary.main" : "background.paper",
                border: "2px solid",
                borderColor: active ? "primary.main" : "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: active ? "primary.contrastText" : "text.secondary",
                zIndex: 2,
            }}
        >
            {icon}
        </Box>
    );
};

const StepsCard = ({
    section,
    expandedStep,
    onStepClick,
}: Props) => {
    const navigate = useNavigate();

    return (
        <Paper
            sx={{
                p: 3,
                borderRadius: 3,
                height: "100%",
            }}
        >
            <Typography variant="h3">
                {section.title}
            </Typography>

            <Box
                sx={{
                    width: "100%",
                    height: 3,
                    bgcolor: "primary.main",
                    mt: 1,
                    mb: 2,
                }}
            />
            <Typography color="text.secondary" mb={4}>
                {section.description}
            </Typography>
            <Stack spacing={3}>
                {section.steps.map((step, index) => {
                    const active = expandedStep === step.id;
                    const last = index === section.steps.length - 1;

                    return (
                        <Stack
                            key={step.id}
                            direction="row"
                            spacing={2}
                            alignItems="flex-start"
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    display: "flex",
                                    justifyContent: "center",
                                    width: 40,
                                    flexShrink: 0,
                                }}
                            >
                                {!last && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 40,
                                            bottom: -24,
                                            width: 2,
                                            bgcolor: "divider",
                                        }}
                                    />
                                )}

                                <CustomStepIcon
                                    active={active}
                                    completed={step.isCompleted}
                                    icon={step.icon}
                                />
                            </Box>

                            <Box flex={1}>
                                <Typography
                                    fontWeight={600}
                                    sx={{
                                        cursor: "pointer",
                                        userSelect: "none",
                                    }}
                                    onClick={() => onStepClick(step.id)}
                                >
                                    {step.title}
                                </Typography>

                                <Collapse in={active}>
                                    <Typography
                                        color="text.secondary"
                                        mt={1}
                                    >
                                        {step.description}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        mt={2}
                                    >
                                        <Button
                                            variant="contained"
                                            onClick={() => {
                                                if (step.onAction) {
                                                    step.onAction();
                                                } else if (step.destination) {
                                                    navigate(step.destination);
                                                }
                                            }}
                                        >
                                            {step.actionText}
                                        </Button>

                                        {step.allowIgnore && (
                                            <Button variant="text">
                                                Omitir
                                            </Button>
                                        )}
                                    </Stack>
                                </Collapse>
                            </Box>
                        </Stack>
                    );
                })}
            </Stack>
        </Paper>
    );
};

export default StepsCard;